import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, AlertCircle, Search, X, Thermometer, CheckCircle, AlertTriangle, Bell, Pencil, LineChart, MoreHorizontal, Signal, Battery, Wrench, Power, Trash2, ListPlus, Upload, SlidersHorizontal, Zap } from 'lucide-react';
import {
  sensorApi,
  useGetSensorListQuery,
  useGetTypesQuery,
  useAddSensorMutation,
  useDeleteSensorMutation,
  useUpdateSensorMutation,
  useSetSensorServiceStatusMutation,
  useUpdateSensorCalibrationMutation,
  useUpdateFirmwareMutation,
} from '../services/sensorApi';
import { useGetBaseUnitListQuery } from '../services/baseUnitApi';
import { useGetLiveDataQuery } from '../services/sensorDataApi';
import { useGetAlarmsQuery } from '../services/alarmApi';
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLogger';
import { formatSensorSerial, isNewerVersion } from '../utils/sensorUtils';
import { getBatteryStatus, getBatteryLowThresholdVolts } from '../utils/batteryUtils';
import { getSignalStatus, getRssiStatus } from '../utils/signalUtils';
import { formatDateTime } from '../utils/dateUtils';
import { Sensor } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { AccessDeniedDialog } from '../components/common/AccessDeniedDialog';
import { Dialog } from '../components/common/Dialog';
import { expandSensors, isSplitRow, SensorRow } from '../utils/expandSensors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { useGetZonesQuery, useAssignSensorMutation, useRemoveSensorMutation } from '../services/zoneApi';
import { useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems, MenuSeparator } from '@headlessui/react';
import { SplitChannelDialog } from '../components/sensors/SplitChannelDialog';
import { CalibrateSensorDialog } from '../components/sensors/CalibrateSensorDialog';
import { SetRefCalDialog } from '../components/sensors/SetRefCalDialog';
import { isSignalTestAllowed, readSignalTestSensors, toggleSignalTestSensor } from '../utils/signalTestMode';

const SUPER_ADMIN_EMAILS = ['adam@thermal-systems.co.uk', 'dave@thermal-systems.co.uk'];

interface AddSensorForm {
  serialNo: string;
  name: string;
  type: string;
  zoneId?: number;
}

const initialFormState: AddSensorForm = {
  serialNo: '',
  name: '',
  type: '1'
};

interface ApiError {
  data?: { message?: string };
  message?: string;
}

export default function SensorsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canManageSensors } = usePermissions();
  const [accessDeniedAction, setAccessDeniedAction] = useState<string | null>(null);
  const [isAddingSensor, setIsAddingSensor] = useState(false);
  const [formData, setFormData] = useState<AddSensorForm>(initialFormState);
  const [searchQuery, setSearchQuery] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [serialError, setSerialError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<{ id: number | null; name: string; type: string; calibrationDate: string; probeType: 'internal' | 'external' }>({ id: null, name: '', type: '', calibrationDate: '', probeType: 'internal' });
  const [statusFilter, setStatusFilter] = useState<'all' | 'online' | 'warnings' | 'offline'>('all');
  const [editingNameId, setEditingNameId] = useState<number | null>(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [splitDialogSensor, setSplitDialogSensor] = useState<Sensor | null>(null);
  const [calibrateSensor, setCalibrateSensor] = useState<Sensor | null>(null);
  const [setRefCalSensor, setSetRefCalSensor] = useState<Sensor | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // ── Bulk add ──────────────────────────────────────────────────────────────
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [bulkAddForm, setBulkAddForm] = useState({ type: '4', namePrefix: 'Sensor', startSerial: '', endSerial: '' });
  const [bulkAddProgress, setBulkAddProgress] = useState<{ current: number; total: number; errors: string[] } | null>(null);

  // ── Bulk delete ───────────────────────────────────────────────────────────
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false);
  const [selectedSensorIds, setSelectedSensorIds] = useState<Set<number>>(new Set());

  const user = useSelector((state: RootState) => state.auth.user);
  const isSuperAdmin = SUPER_ADMIN_EMAILS.includes(user?.email ?? '');
  const isSignalTestUser = isSignalTestAllowed(user?.email);
  const [signalTestSensors, setSignalTestSensors] = useState(() => readSignalTestSensors());

  const { data: sensors, isLoading, error, refetch: refetchSensors } = useGetSensorListQuery();
  // Align with Dashboard: same rolling window so online/offline counts match
  const { data: liveDataFeed = [] } = useGetLiveDataQuery({ timeWindow: 5 }, { skip: !user });
  const { data: types } = useGetTypesQuery();
  const { data: alarms } = useGetAlarmsQuery(undefined, { skip: !user });
  const [addSensor] = useAddSensorMutation();
  const [deleteSensor] = useDeleteSensorMutation();
  const [updateSensor] = useUpdateSensorMutation();
  const [setSensorServiceStatus] = useSetSensorServiceStatusMutation();
  const [updateSensorCalibration] = useUpdateSensorCalibrationMutation();
  const [updateFirmware] = useUpdateFirmwareMutation();
  const { data: baseUnits = [] } = useGetBaseUnitListQuery(undefined, { skip: !isSuperAdmin });
  // One sensor from its card, or several from bulk select. 'keep' (bulk only)
  // sends each sensor the build for the probe it was last given.
  const [firmwareDialogSensors, setFirmwareDialogSensors] = useState<Sensor[]>([]);
  const [firmwareBaseUnitSerial, setFirmwareBaseUnitSerial] = useState('');
  const [firmwareProbeType, setFirmwareProbeType] = useState<'keep' | 'internal' | 'external'>('internal');
  const [firmwareResults, setFirmwareResults] = useState<Record<number, { ok: boolean; text: string }>>({});
  const [firmwareWifiSsid, setFirmwareWifiSsid] = useState('');
  const [firmwareWifiPassword, setFirmwareWifiPassword] = useState('');
  const [firmwareShowPassword, setFirmwareShowPassword] = useState(false);
  const [firmwareSubmitting, setFirmwareSubmitting] = useState(false);
  const [firmwareError, setFirmwareError] = useState<string | null>(null);
  const [firmwareSuccess, setFirmwareSuccess] = useState(false);
  const [oosOverrides, setOosOverrides] = useState<Record<number, boolean>>({});
  const { data: zones } = useGetZonesQuery();
  const [assignSensor] = useAssignSensorMutation();
  const [removeSensor] = useRemoveSensorMutation();

  useEffect(() => {
    if (!sensors) return;
    setOosOverrides(prev => {
      const next = { ...prev };
      let changed = false;
      for (const id in next) {
        const match = sensors.find(s => s.id === Number(id));
        if (match && !!match.is_out_of_service === next[id]) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sensors]);

  const sensorsWithOverrides = useMemo(() => {
    if (!sensors) return [];
    return sensors.map(s =>
      oosOverrides[s.id] !== undefined ? { ...s, is_out_of_service: oosOverrides[s.id] } : s
    );
  }, [sensors, oosOverrides]);

  const alarmMap = useMemo(() => {
    if (!alarms || !Array.isArray(alarms)) return {} as Record<number, any>;
    return alarms.reduce((acc: Record<number, any>, alarm: any) => {
      acc[alarm.sensor_id] = alarm;
      return acc;
    }, {});
  }, [alarms]);

  const filteredSensors = useMemo(() => {
    if (!sensorsWithOverrides) return [];
    let result = sensorsWithOverrides;
    if (statusFilter !== 'all') {
      result = result.filter(s => {
        const oos = !!s.is_out_of_service;
        if (oos) return false;
        const liveSensorData = liveDataFeed.find(ld => Number(ld.sensor_id) === s.serialNo);
        const batteryValue = liveSensorData?.Battery ?? s.Battery;
        const batt = batteryValue ? parseFloat(String(batteryValue)) : 0;
        const hasReading = !!liveSensorData?.timestamp;
        const isOnline = hasReading && (getBatteryLowThresholdVolts(s.type) == null || batt > 0);
        switch (statusFilter) {
          case 'online':   return isOnline;
          case 'warnings': return isOnline && batt > 0 && batt < 30;
          case 'offline':  return !isOnline;
          default: return true;
        }
      });
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(s =>
        s.name.toLowerCase().includes(query) ||
        formatSensorSerial(s.serialNo, s.type).toLowerCase().includes(query)
      );
    }
    return result;
  }, [sensorsWithOverrides, searchQuery, statusFilter, liveDataFeed]);

  const sensorStatCounts = useMemo(() => {
    if (!sensors) return { total: 0, online: 0, warnings: 0, offline: 0 };
    let online = 0;
    let warnings = 0;
    let offline = 0;
    for (const s of sensors) {
      if (s.is_out_of_service === true) continue;
      const liveSensorData = liveDataFeed.find(ld => Number(ld.sensor_id) === s.serialNo);
      const batteryValue = liveSensorData?.Battery ?? s.Battery;
      const batt = batteryValue ? parseFloat(String(batteryValue)) : 0;
      const hasReading = !!liveSensorData?.timestamp;
      if (hasReading && (getBatteryLowThresholdVolts(s.type) == null || batt > 0)) {
        online++;
        if (batt < 30) warnings++;
      } else {
        offline++;
      }
    }
    return { total: sensors.length, online, warnings, offline };
  }, [sensors, liveDataFeed]);

  // ── Inline rename ──────────────────────────────────────────────────────────
  const startInlineRename = (sensor: Sensor) => {
    if (!canManageSensors) { setAccessDeniedAction('Editing sensors'); return; }
    setEditingNameId(sensor.id);
    setEditingNameValue(sensor.name);
    setTimeout(() => renameInputRef.current?.select(), 30);
  };

  const saveInlineRename = async () => {
    if (!editingNameId || !editingNameValue.trim()) return;
    setFormError(null);
    try {
      await updateSensor({ sensorId: editingNameId, sensorName: editingNameValue.trim() }).unwrap();
      setEditingNameId(null);
    } catch (error) {
      let msg = 'Failed to rename sensor.';
      if (typeof error === 'object' && error !== null && 'data' in error && (error as any).data?.message)
        msg = (error as any).data.message;
      setFormError(msg);
    }
  };

  const cancelInlineRename = () => { setEditingNameId(null); setEditingNameValue(''); };

  // ── Edit modal (name + type) ───────────────────────────────────────────────
  const handleEdit = (sensor: Sensor) => {
    if (!canManageSensors) { setAccessDeniedAction('Editing sensors'); return; }
    setEditFormData({
      id: sensor.id,
      name: sensor.name,
      type: sensor.type,
      calibrationDate: sensor.calibration_date ? sensor.calibration_date.slice(0, 10) : '',
      probeType: sensor.probe_type ?? 'internal',
    });
    setIsEditModalOpen(true);
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditModalSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFormData.id) return;
    setFormError(null);
    try {
      await updateSensor({ sensorId: editFormData.id, sensorName: editFormData.name }).unwrap();

      const original = sensors?.find(s => s.id === editFormData.id);
      const originalCalibrationDate = original?.calibration_date ? original.calibration_date.slice(0, 10) : '';
      if (editFormData.calibrationDate !== originalCalibrationDate) {
        const calibrationDate = editFormData.calibrationDate || null;
        await updateSensorCalibration({ sensorId: editFormData.id, calibrationDate }).unwrap();
        await logAuditEvent(
          calibrationDate ? AUDIT_ACTIONS.SENSOR_CALIBRATE : AUDIT_ACTIONS.SENSOR_CALIBRATE_CLEAR,
          calibrationDate
            ? `Sensor calibration date set to ${calibrationDate} - Sensor ID: ${editFormData.id}, Name: ${editFormData.name}`
            : `Sensor calibration date cleared - Sensor ID: ${editFormData.id}, Name: ${editFormData.name}`,
          user
        );
      }

      setIsEditModalOpen(false);
      setEditFormData({ id: null, name: '', type: '', calibrationDate: '', probeType: 'internal' });
    } catch (error) {
      let errorMessage = 'Failed to update sensor. Please try again.';
      if (typeof error === 'object' && error !== null && 'data' in error && (error as any).data?.message)
        errorMessage = (error as any).data.message;
      else if (error instanceof Error) errorMessage = error.message;
      setFormError(errorMessage);
    }
  };

  const handleEditModalCancel = () => {
    setIsEditModalOpen(false);
    setEditFormData({ id: null, name: '', type: '', calibrationDate: '', probeType: 'internal' });
  };

  // ── OOS toggle ────────────────────────────────────────────────────────────
  const handleToggleServiceStatus = async (sensor: Sensor) => {
    if (!canManageSensors) { setAccessDeniedAction('Changing sensor service status'); return; }
    const current = oosOverrides[sensor.id] ?? !!sensor.is_out_of_service;
    const next = !current;
    if (!window.confirm(`Are you sure you want to ${next ? 'mark this sensor as out of service' : 'return this sensor to service'}?`)) return;
    setOosOverrides(prev => ({ ...prev, [sensor.id]: next }));
    try {
      await setSensorServiceStatus({ sensorId: sensor.id, isOutOfService: next }).unwrap();
      await logAuditEvent(
        next ? AUDIT_ACTIONS.SENSOR_SET_OUT_OF_SERVICE : AUDIT_ACTIONS.SENSOR_RETURN_TO_SERVICE,
        `Sensor ${next ? 'marked out of service' : 'returned to service'} - Sensor ID: ${sensor.id}, Name: ${sensor.name}`,
        user
      );
    } catch (error) {
      setFormError('Failed to update sensor service status. Please try again.');
      setOosOverrides(prev => { const copy = { ...prev }; delete copy[sensor.id]; return copy; });
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (sensor: Sensor) => {
    if (!canManageSensors) { setAccessDeniedAction('Deleting sensors'); return; }
    if (!window.confirm('Are you sure you want to delete this sensor?')) return;
    try {
      setFormError(null);
      if (zones) {
        for (const zone of zones) {
          try { await removeSensor({ zoneId: zone.zone_id, sensorId: sensor.id }).unwrap(); } catch {}
        }
      }
      await deleteSensor(sensor).unwrap();
    } catch (error) {
      let errorMessage = 'Failed to delete sensor. Please try again.';
      if (typeof error === 'object' && error !== null && 'data' in error && (error as any).data?.message)
        errorMessage = (error as any).data.message;
      else if (error instanceof Error) errorMessage = error.message;
      setFormError(errorMessage);
    }
  };

  // ── Firmware update ───────────────────────────────────────────────────────
  const isFirmwareTarget = (s: Sensor) => s.type === '1';

  // Newest version any TS1 sensor reports; cards running older show amber
  const newestSensorFirmware = useMemo(() => {
    let newest = '';
    for (const s of sensors ?? []) {
      if (isFirmwareTarget(s) && s.firmware_version && (!newest || isNewerVersion(s.firmware_version, newest))) {
        newest = s.firmware_version;
      }
    }
    return newest;
  }, [sensors]);

  const openFirmwareDialog = (targets: Sensor[]) => {
    if (targets.length === 0) return;
    setFirmwareDialogSensors(targets);
    setFirmwareBaseUnitSerial(baseUnits[0] ? String(baseUnits[0].serialNo) : '');
    setFirmwareProbeType(targets.length === 1 ? (targets[0].probe_type ?? 'internal') : 'keep');
    setFirmwareResults({});
    setFirmwareWifiSsid('');
    setFirmwareWifiPassword('');
    setFirmwareShowPassword(false);
    setFirmwareError(null);
    setFirmwareSuccess(false);
  };

  const handleFirmwareSubmit = async () => {
    if (firmwareDialogSensors.length === 0 || !firmwareBaseUnitSerial || !firmwareWifiSsid || !firmwareWifiPassword) return;
    setFirmwareSubmitting(true);
    setFirmwareError(null);
    // Retry resends only the sensors that failed last time
    const pending = firmwareDialogSensors.filter(s => !firmwareResults[s.id]?.ok);
    let failures = 0;
    // One at a time: each send writes the sensor's build assignment and
    // publishes its own updatesensor command; bases flag each sensor separately.
    for (const [i, sensor] of pending.entries()) {
      const probeType = firmwareProbeType === 'keep' ? (sensor.probe_type ?? 'internal') : firmwareProbeType;
      let outcome: { ok: boolean; text: string };
      try {
        const result = await updateFirmware({
          sensorSerialNumber: sensor.serialNo,
          baseUnitSerialNumbers: [Number(firmwareBaseUnitSerial)],
          wifi: { ssid: firmwareWifiSsid, password: firmwareWifiPassword },
          probeType,
        }).unwrap();
        outcome = { ok: true, text: `${probeType} v${result.targetVersion}` };
        await logAuditEvent(
          AUDIT_ACTIONS.SENSOR_PROBE_TYPE_CHANGE,
          `Sensor firmware update sent: ${probeType}-probe build v${result.targetVersion} - Serial: ${sensor.serialNo}, Name: ${sensor.name}`,
          user
        );
      } catch (err: any) {
        failures++;
        outcome = { ok: false, text: err?.data?.message || err?.message || 'Firmware update failed' };
      }
      setFirmwareResults(prev => ({ ...prev, [sensor.id]: outcome }));
      if (i < pending.length - 1) await new Promise(r => setTimeout(r, 400));
    }
    setFirmwareSubmitting(false);
    if (failures === 0) setFirmwareSuccess(true);
    else setFirmwareError(`${failures} of ${pending.length} could not be sent; see the list above.`);
  };

  // ── Bulk add ──────────────────────────────────────────────────────────────
  const handleBulkAdd = async () => {
    if (!canManageSensors) { setAccessDeniedAction('Adding sensors'); return; }
    const start = parseInt(bulkAddForm.startSerial, 10);
    const end = parseInt(bulkAddForm.endSerial, 10);
    if (isNaN(start) || isNaN(end) || start > end) return;
    if (end - start >= 500) { alert('Maximum 500 sensors per bulk add.'); return; }

    const total = end - start + 1;
    setBulkAddProgress({ current: 0, total, errors: [] });
    const errors: string[] = [];

    for (let serial = start; serial <= end; serial++) {
      const name = bulkAddForm.namePrefix.trim()
        ? `${bulkAddForm.namePrefix.trim()} ${serial}`
        : String(serial);
      try {
        const created = await addSensor({ serialNo: serial, name, type: bulkAddForm.type }).unwrap();
        dispatch(
          sensorApi.util.updateQueryData('getSensorList', undefined, (draft) => {
            const idNum = Number(created?.id);
            if (created?.id && !draft.some(s => Number(s.id) === idNum)) {
              draft.push({
                id: idNum, serialNo: serial, name, type: String(bulkAddForm.type),
                created_at: new Date().toISOString(), updated_at: null,
                is_deleted: false, is_out_of_service: false, RSSI: null, SNR: null, Battery: '',
              });
            }
          })
        );
      } catch (err: any) {
        errors.push(`Serial ${serial}: ${err?.data?.message || err?.message || 'Failed'}`);
      }
      setBulkAddProgress({ current: serial - start + 1, total, errors: [...errors] });
      await new Promise(r => setTimeout(r, 30));
    }
  };

  // ── Bulk delete ───────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (!canManageSensors) { setAccessDeniedAction('Deleting sensors'); return; }
    if (selectedSensorIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedSensorIds.size} sensor${selectedSensorIds.size !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    const toDelete = sensors?.filter(s => selectedSensorIds.has(s.id)) ?? [];
    setFormError(null);
    for (const sensor of toDelete) {
      try {
        if (zones) {
          for (const zone of zones) {
            try { await removeSensor({ zoneId: zone.zone_id, sensorId: sensor.id }).unwrap(); } catch {}
          }
        }
        await deleteSensor(sensor).unwrap();
      } catch (err: any) {
        setFormError(`Some sensors could not be deleted: ${err?.data?.message || err?.message || 'Unknown error'}`);
      }
    }
    setSelectedSensorIds(new Set());
    setIsBulkSelectMode(false);
  };

  const toggleSensorSelection = (id: number) => {
    setSelectedSensorIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Add sensor serial helpers ──────────────────────────────────────────────
  const getSerialPattern = (type: string) => {
    if (type === 'TS1' || type === '4') return /^\d{2}\.\d{2}\.\d{2}$/;
    if (['EMS IceSpy', 'EMS IceSpy Humidity', 'EMS IceSpy Dual', '2', '3', '6', '7'].includes(type)) return /^\d{2}\.\d{2}\.\d{2}\.\d{2}$/;
    return null;
  };

  const getSerialPlaceholder = (type: string) => {
    if (type === 'TS1' || type === '4') return 'e.g. 12.34.56';
    if (['EMS IceSpy', 'EMS IceSpy Humidity', 'EMS IceSpy Dual', '2', '3', '6', '7'].includes(type)) return 'e.g. 12.34.56.78';
    return '';
  };

  const validateSerial = (serial: string, type: string) => {
    const pattern = getSerialPattern(type);
    if (!pattern) return true;
    return pattern.test(serial);
  };

  const maskSerialInput = (value: string, type: string) => {
    const digits = value.replace(/\D/g, '');
    if (type === 'TS1' || type === '4') {
      return digits.slice(0, 6).replace(/(\d{2})(\d{2})(\d{0,2})/, (m, p1, p2, p3) =>
        p3 ? `${p1}.${p2}.${p3}` : `${p1}.${p2}`.replace(/\.$/, ''));
    }
    if (['EMS IceSpy', 'EMS IceSpy Humidity', 'EMS IceSpy Dual', '2', '3', '6', '7'].includes(type)) {
      return digits.slice(0, 8).replace(/(\d{2})(\d{2})(\d{2})(\d{0,2})/, (m, p1, p2, p3, p4) =>
        p4 ? `${p1}.${p2}.${p3}.${p4}` : `${p1}.${p2}.${p3}`.replace(/\.$/, ''));
    }
    return digits;
  };

  const handleSerialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = maskSerialInput(e.target.value, formData.type);
    setFormData({ ...formData, serialNo: value });
    setSerialError(formData.type && !validateSerial(value, formData.type) ? 'Invalid serial format for this sensor type.' : null);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, type: e.target.value, serialNo: '' });
    setSerialError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageSensors) { setAccessDeniedAction('Adding sensors'); return; }
    setFormError(null);
    if (!formData.type) { setSerialError('Please select a sensor type first.'); return; }
    if (!validateSerial(formData.serialNo, formData.type)) { setSerialError('Invalid serial format for this sensor type.'); return; }
    setSerialError(null);
    try {
      const serialNumberStripped = formData.serialNo.replace(/\./g, '');
      const serialNumber = parseInt(serialNumberStripped);
      if (isNaN(serialNumber)) throw new Error('Serial number must be a valid number');

      const created = await addSensor({ serialNo: serialNumber, name: formData.name, type: formData.type }).unwrap();
      let newSensorId = created?.id;
      if (newSensorId == null || !Number.isFinite(Number(newSensorId))) {
        const { data: sensorsList } = await refetchSensors();
        const found = sensorsList?.find((s: Sensor) => Number(s.serialNo) === serialNumber);
        if (!found?.id) throw new Error('Could not find newly created sensor');
        newSensorId = found.id;
      }

      dispatch(
        sensorApi.util.updateQueryData('getSensorList', undefined, (draft) => {
          const idNum = Number(newSensorId);
          if (draft.some((s) => Number(s.id) === idNum)) return;
          draft.push({
            id: idNum, serialNo: serialNumber, name: formData.name,
            type: String(created.type ?? formData.type),
            created_at: new Date().toISOString(), updated_at: null,
            is_deleted: false, is_out_of_service: false, RSSI: null, SNR: null, Battery: '',
          });
        }),
      );

      if (formData.zoneId) {
        try {
          await assignSensor({ zoneId: formData.zoneId, sensorId: Number(newSensorId) }).unwrap();
        } catch (zoneError: any) {
          setFormError(zoneError?.data?.message || zoneError?.message || 'Failed to assign sensor to zone');
          return;
        }
      }

      setFormData(initialFormState);
      setIsAddingSensor(false);
    } catch (error: unknown) {
      const err = error as ApiError;
      setFormError(err?.data?.message || err?.message || 'Failed to add sensor');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-app-canvas px-6 py-10 dark:bg-gray-900">
        <div className="mx-auto max-w-3xl space-y-4 animate-pulse">
          <div className="h-9 max-w-xs rounded-xl bg-gray-200 dark:bg-gray-700" />
          <div className="h-4 max-w-lg rounded-lg bg-gray-200/90 dark:bg-gray-700/80" />
          <div className="mt-8 flex gap-3">
            {[1, 2, 3, 4].map(n => <div key={n} className="h-10 flex-1 rounded-full bg-gray-200 dark:bg-gray-700" />)}
          </div>
          <div className="h-48 rounded-2xl bg-gray-200/70 dark:bg-gray-800/80" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-400">Failed to load sensors</span>
        </div>
      </div>
    );
  }

  const expandedRows = expandSensors(filteredSensors);

  const allVisibleSelected = filteredSensors.length > 0 && filteredSensors.every(s => selectedSensorIds.has(s.id));
  const toggleSelectAll = () => {
    if (allVisibleSelected) setSelectedSensorIds(new Set());
    else setSelectedSensorIds(new Set(filteredSensors.map(s => s.id)));
  };
  const outdatedVisibleSensors = filteredSensors.filter(s =>
    isFirmwareTarget(s) && !!newestSensorFirmware && (!s.firmware_version || isNewerVersion(newestSensorFirmware, s.firmware_version))
  );
  const selectedFirmwareTargets = (sensors ?? []).filter(s => selectedSensorIds.has(s.id) && isFirmwareTarget(s));

  return (
    <div className="min-h-screen bg-app-canvas dark:bg-gray-900 min-w-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-8 min-w-0 max-w-full">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="mb-6 rounded-2xl border border-gray-200/70 bg-gradient-to-br from-white to-gray-50/90 px-5 py-5 shadow-sm dark:border-gray-700/60 dark:from-gray-900 dark:to-gray-950/80 sm:px-6 md:mb-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 dark:bg-indigo-950/50">
                  <Thermometer className="h-5 w-5 text-indigo-600 dark:text-indigo-400" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white sm:text-2xl md:text-3xl md:font-bold">
                    Sensor Management
                  </h1>
                  <p className="mt-1 max-w-xl text-sm text-gray-600 dark:text-gray-400">
                    {canManageSensors
                      ? 'Add sensors, rename them, then set alarm thresholds to complete setup.'
                      : 'Browse sensor names, telemetry status, and connectivity.'}
                  </p>
                </div>
              </div>
              {/* Status filter chips */}
              <div className="mt-4 flex flex-wrap items-center gap-2 md:gap-2.5" role="group" aria-label="Filter sensors">
                {([
                  { key: 'all', label: 'All sensors', count: sensorStatCounts.total, icon: null, active: 'border-indigo-300 bg-white text-indigo-900 shadow-indigo-100/60 dark:border-indigo-700/70 dark:bg-indigo-950/50 dark:text-indigo-100' },
                  { key: 'online', label: 'Online', count: sensorStatCounts.online, icon: <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />, active: 'border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100' },
                  { key: 'warnings', label: 'Warnings', count: sensorStatCounts.warnings, icon: <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />, active: 'border-amber-400 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/35 dark:text-amber-50' },
                  { key: 'offline', label: 'Offline', count: sensorStatCounts.offline, icon: <X className="h-3.5 w-3.5 text-red-600 dark:text-red-400 opacity-90" />, active: 'border-red-400 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100' },
                ] as const).map(chip => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setStatusFilter(statusFilter === chip.key && chip.key !== 'all' ? 'all' : chip.key as any)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/55 ${
                      statusFilter === chip.key
                        ? chip.active
                        : 'border-gray-200/90 bg-gray-50/80 text-gray-700 hover:bg-white dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-800/90'
                    }`}
                  >
                    {chip.icon}
                    {chip.label}
                    <span className="rounded-md bg-black/[0.06] px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-700 dark:bg-white/10 dark:text-gray-300">
                      {chip.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>
            {canManageSensors && (
              <div className="flex flex-wrap gap-2 sm:flex-nowrap">
                <button
                  type="button"
                  onClick={() => setIsAddingSensor(true)}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-600/25 transition hover:bg-indigo-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 sm:flex-none"
                >
                  <Plus className="h-5 w-5 shrink-0" aria-hidden />
                  Add sensor
                </button>
                <button
                  type="button"
                  onClick={() => { setBulkAddProgress(null); setBulkAddForm({ type: '4', namePrefix: 'Sensor', startSerial: '', endSerial: '' }); setIsBulkAdding(true); }}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-indigo-200 bg-white px-4 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-indigo-800 dark:bg-gray-900 dark:text-indigo-300 dark:hover:bg-indigo-950/40 sm:flex-none"
                >
                  <ListPlus className="h-5 w-5 shrink-0" aria-hidden />
                  Bulk add
                </button>
                <button
                  type="button"
                  onClick={() => { setIsBulkSelectMode(m => !m); setSelectedSensorIds(new Set()); }}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:flex-none ${
                    isBulkSelectMode
                      ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <Trash2 className="h-4 w-4 shrink-0" aria-hidden />
                  {isBulkSelectMode ? 'Cancel' : isSuperAdmin ? 'Bulk select' : 'Bulk delete'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Search ──────────────────────────────────────────────────────── */}
        <div className="mb-6 rounded-2xl border border-gray-200/80 bg-white/90 p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900/85 sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <label htmlFor="sensor-search" className="sr-only">Search sensors</label>
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" aria-hidden />
              </div>
              <input
                id="sensor-search"
                type="text"
                placeholder="Search by name or serial..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                autoComplete="off"
                className="block w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 pl-10 pr-10 text-[15px] text-gray-900 placeholder-gray-500 shadow-inner transition dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" aria-label="Clear search">
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
            {(searchQuery || statusFilter !== 'all') && (
              <button
                type="button"
                onClick={() => { setStatusFilter('all'); setSearchQuery(''); }}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
              >
                Clear filters
              </button>
            )}
          </div>
          {(searchQuery || statusFilter !== 'all') && (
            <p className="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-600 dark:border-gray-700 dark:text-gray-400">
              Showing <span className="font-semibold tabular-nums text-gray-800 dark:text-gray-200">{expandedRows.length}</span> result{expandedRows.length !== 1 ? 's' : ''}
              {statusFilter !== 'all' && <span className="capitalize"> · {statusFilter}</span>}
              {searchQuery && <> matching &ldquo;{searchQuery}&rdquo;</>}
            </p>
          )}
        </div>

        {/* ── Form errors ──────────────────────────────────────────────────── */}
        {formError && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-950/30">
            <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
            <p className="text-sm text-red-700 dark:text-red-400">{formError}</p>
            <button type="button" onClick={() => setFormError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* ── Sensor management cards ───────────────────────────────────────── */}
        {expandedRows.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 py-16 text-center">
            <Thermometer className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" />
            <p className="mt-4 text-base font-medium text-gray-600 dark:text-gray-400">
              {searchQuery || statusFilter !== 'all' ? 'No sensors match your filters' : 'No sensors registered yet'}
            </p>
            {canManageSensors && !searchQuery && statusFilter === 'all' && (
              <button
                onClick={() => setIsAddingSensor(true)}
                className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
              >
                <Plus className="h-4 w-4" />
                Add your first sensor
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2.5">
            {isBulkSelectMode && filteredSensors.length > 0 && (
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {allVisibleSelected ? 'Deselect all' : 'Select all'} ({filteredSensors.length})
                </span>
                {isSuperAdmin && outdatedVisibleSensors.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedSensorIds(new Set(outdatedVisibleSensors.map(s => s.id)))}
                    className="rounded-lg border border-amber-200 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-950/40"
                    title={`Select TS1 sensors not reporting v${newestSensorFirmware}`}
                  >
                    Select below v{newestSensorFirmware} ({outdatedVisibleSensors.length})
                  </button>
                )}
                {selectedSensorIds.size > 0 && (
                  <span className="ml-auto text-sm font-medium text-gray-500 dark:text-gray-400">
                    {selectedSensorIds.size} selected
                  </span>
                )}
              </div>
            )}
            {expandedRows.map((sensor: SensorRow) => {
              const cardKey = isSplitRow(sensor) ? `${sensor.id}-ch${sensor._channel}` : String(sensor.id);
              const reading = liveDataFeed.find(d => Number(d.sensor_id) === sensor.serialNo);
              const alarm = alarmMap[sensor.id];
              const isInlineEditing = editingNameId === sensor.id && !isSplitRow(sensor);
              const isOos = !!sensor.is_out_of_service;

              const effHigh = isSplitRow(sensor) ? sensor._channelConfig?.high_alarm : alarm?.high_alarm;
              const effLow = isSplitRow(sensor) ? sensor._channelConfig?.low_alarm : alarm?.low_alarm;
              const hasThresholds = (effHigh != null && effHigh !== '') || (effLow != null && effLow !== '');

              const battVal = reading?.Battery ?? sensor.Battery;
              const battNum = battVal != null ? parseFloat(String(battVal)) : NaN;
              const battStatus = !isNaN(battNum) ? getBatteryStatus(sensor.type, battNum) : null;
              const hasLiveReading = !!reading?.timestamp;
              const isOnline =
                !isOos && hasLiveReading && (getBatteryLowThresholdVolts(sensor.type) == null || (!Number.isNaN(battNum) && battNum > 0));

              const rssi = reading?.RSSI ?? sensor.RSSI;
              const snr = reading?.SNR ?? sensor.SNR;
              const sigStatus = snr ? getSignalStatus(snr) : getRssiStatus(rssi);
              const typeLabel = types?.find(t => t.type === sensor.type)?.description ?? `Type ${sensor.type}`;
              const isDualChannel = sensor.type === '5' || sensor.type === '6';

              return (
                <div
                  key={cardKey}
                  className={`rounded-xl border bg-white dark:bg-gray-900 shadow-sm overflow-hidden transition-opacity ${isOos ? 'opacity-60' : ''} ${
                    !isSplitRow(sensor) && signalTestSensors.has(sensor.id)
                      ? 'border-orange-400 dark:border-orange-500 ring-1 ring-orange-400/40'
                      : 'border-gray-200/80 dark:border-gray-700'
                  }`}
                >
                  {/* ── Card body ──────────────────────────────────────────── */}
                  <div className="flex items-start gap-3 px-4 pt-4 pb-3">
                    {/* Bulk select checkbox */}
                    {isBulkSelectMode && !isSplitRow(sensor) && (
                      <input
                        type="checkbox"
                        checked={selectedSensorIds.has(sensor.id)}
                        onChange={() => toggleSensorSelection(sensor.id)}
                        onClick={e => e.stopPropagation()}
                        className="mt-[3px] h-4 w-4 flex-shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600"
                      />
                    )}
                    {/* Online/offline dot */}
                    <div
                      className={`mt-[5px] h-2.5 w-2.5 flex-shrink-0 rounded-full ${
                        isOos
                          ? 'bg-gray-400'
                          : isOnline
                            ? 'bg-emerald-500 shadow-sm shadow-emerald-500/40'
                            : !Number.isNaN(battNum) && battNum > 0
                              ? 'bg-amber-400'
                              : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                      title={
                        isOos
                          ? 'Out of service'
                          : isOnline
                            ? 'Online'
                            : !Number.isNaN(battNum) && battNum > 0
                              ? 'No recent reading in live feed'
                              : 'Offline'
                      }
                    />

                    {/* Name + serial + type ─────────────────────────────── */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {isInlineEditing ? (
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <input
                              ref={renameInputRef}
                              value={editingNameValue}
                              onChange={e => setEditingNameValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveInlineRename();
                                if (e.key === 'Escape') cancelInlineRename();
                              }}
                              className="flex-1 min-w-0 rounded-lg border border-indigo-400 bg-white dark:bg-gray-800 px-2.5 py-1 text-sm font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                              autoFocus
                            />
                            <button type="button" onClick={saveInlineRename} className="flex-shrink-0 rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-indigo-500 transition-colors">
                              Save
                            </button>
                            <button type="button" onClick={cancelInlineRename} className="flex-shrink-0 rounded-lg border border-gray-200 dark:border-gray-600 px-2.5 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <>
                            <span
                              className={`text-sm font-semibold text-gray-900 dark:text-white truncate cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors ${isOos ? 'line-through text-gray-500' : ''}`}
                              onClick={() => navigate(isSplitRow(sensor) ? `/sensors/${sensor.id}?channel=${sensor._channel}` : `/sensors/${sensor.id}`)}
                              title="View graph"
                            >
                              {sensor.name}
                            </span>
                            {canManageSensors && !isSplitRow(sensor) && !isOos && (
                              <button
                                type="button"
                                onClick={() => startInlineRename(sensor)}
                                className="flex-shrink-0 rounded p-0.5 text-gray-300 hover:text-indigo-500 dark:text-gray-600 dark:hover:text-indigo-400 transition-colors"
                                title="Rename this sensor"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-mono text-gray-400 dark:text-gray-500">
                          {formatSensorSerial(sensor.serialNo, sensor.type)}
                        </span>
                        <span className="text-gray-300 dark:text-gray-600">·</span>
                        <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-gray-400">
                          {typeLabel}
                          {isSplitRow(sensor) && (
                            <span className="ml-1 text-indigo-500 dark:text-indigo-400">Ch {sensor._channel}</span>
                          )}
                        </span>
                        {isSuperAdmin && isFirmwareTarget(sensor) && !isSplitRow(sensor) && (() => {
                          // Firmware the sensor last reported, plus the probe its latest
                          // reading came from (TY:). Amber when behind the newest version
                          // seen, or when the probe differs from the build last sent.
                          const fw = sensor.firmware_version;
                          const reportedProbe =
                            reading?.data_type === 'external_temp' ? 'external'
                            : reading?.data_type === 'internal_temp' ? 'internal'
                            : null;
                          const behind = !!fw && !!newestSensorFirmware && isNewerVersion(newestSensorFirmware, fw);
                          const probeMismatch = !!reportedProbe && !!sensor.probe_type && reportedProbe !== sensor.probe_type;
                          const warn = !fw || behind || probeMismatch;
                          const title = [
                            fw ? `Firmware ${fw}` : 'No firmware version reported yet',
                            behind ? `older than ${newestSensorFirmware}` : null,
                            reportedProbe ? `reading from the ${reportedProbe} probe` : null,
                            probeMismatch ? `last update sent was the ${sensor.probe_type} build` : null,
                          ].filter(Boolean).join(' · ');
                          return (
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-mono font-medium ${
                                warn
                                  ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400'
                                  : 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950/30 dark:text-purple-300'
                              }`}
                              title={title}
                            >
                              FW {fw ? `v${fw}` : '—'}
                              {reportedProbe && <> · {reportedProbe === 'external' ? 'Ext' : 'Int'}</>}
                            </span>
                          );
                        })()}
                        {isOos && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 dark:bg-gray-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
                            <Wrench className="h-2.5 w-2.5" />
                            OOS
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: thresholds + telemetry ──────────────────────── */}
                    <div className={`flex-shrink-0 flex flex-col items-end gap-1.5 min-w-0${isInlineEditing ? ' hidden' : ''}`}>
                      {/* Threshold badges */}
                      {hasThresholds ? (
                        <div className="flex items-center gap-1 text-[11px] font-mono font-semibold">
                          {(effHigh != null && effHigh !== '') && (
                            <span className="rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 px-1.5 py-0.5 text-red-700 dark:text-red-400">
                              H {effHigh}°
                            </span>
                          )}
                          {(effLow != null && effLow !== '') && (
                            <span className="rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 px-1.5 py-0.5 text-blue-700 dark:text-blue-400">
                              L {effLow}°
                            </span>
                          )}
                        </div>
                      ) : canManageSensors ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400 whitespace-nowrap">
                          No thresholds
                        </span>
                      ) : null}

                      {/* Signal + battery */}
                      <div className="hidden sm:flex items-center gap-2">
                        {sigStatus && rssi != null && (
                          <Signal className={`h-3.5 w-3.5 ${sigStatus.iconColor}`} title={sigStatus.text} />
                        )}
                        {battStatus && (
                          <span className={`text-xs font-medium ${battStatus.color}`}>{battStatus.text}</span>
                        )}
                      </div>

                      {/* Last seen */}
                      {reading?.timestamp && (
                        <span className="hidden md:block text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                          {formatDateTime(reading.timestamp, true)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Action strip ───────────────────────────────────────── */}
                  <div className="flex items-center gap-0.5 border-t border-gray-100 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-950/30 px-3 py-1.5">
                    {/* View graph */}
                    <button
                      type="button"
                      onClick={() => navigate(isSplitRow(sensor) ? `/sensors/${sensor.id}?channel=${sensor._channel}` : `/sensors/${sensor.id}`)}
                      className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                    >
                      <LineChart className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">View graph</span>
                      <span className="sm:hidden">Graph</span>
                    </button>

                    {/* Set thresholds — highlighted amber when not yet configured */}
                    {canManageSensors && (
                      <button
                        type="button"
                        onClick={() => navigate('/alarms', { state: { highlightSensorId: sensor.id } })}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                          !hasThresholds
                            ? 'text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40'
                            : 'text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40'
                        }`}
                      >
                        <Bell className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{!hasThresholds ? 'Set thresholds' : 'Thresholds'}</span>
                        <span className="sm:hidden">Thresholds</span>
                      </button>
                    )}

                    {/* Rename (non-split only) */}
                    {canManageSensors && !isSplitRow(sensor) && !isOos && !isInlineEditing && (
                      <button
                        type="button"
                        onClick={() => startInlineRename(sensor)}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Rename
                      </button>
                    )}

                    {/* Signal Test Mode toggle — only for allowed admin emails */}
                    {isSignalTestUser && !isSplitRow(sensor) && (
                      <button
                        type="button"
                        onClick={() => setSignalTestSensors(toggleSignalTestSensor(sensor.id))}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                          signalTestSensors.has(sensor.id)
                            ? 'bg-orange-100 dark:bg-orange-950/50 text-orange-700 dark:text-orange-300'
                            : 'text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/40'
                        }`}
                        title={signalTestSensors.has(sensor.id) ? 'Disable signal test mode' : 'Enable signal test mode — shows live RSSI/SNR and exact timestamps on the Dashboard'}
                      >
                        <Signal className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{signalTestSensors.has(sensor.id) ? 'Test: ON' : 'Sig Test'}</span>
                      </button>
                    )}

                    {/* Firmware update — superadmin only, type 1 sensors */}
                    {isSuperAdmin && sensor.type === '1' && !isSplitRow(sensor) && (
                      <button
                        type="button"
                        onClick={() => openFirmwareDialog([sensor])}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-purple-700 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/40 transition-colors"
                        title="Push an OTA firmware update to this sensor"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Update FW</span>
                      </button>
                    )}

                    {/* Calibrate — superadmin only, type 1 sensors. Sends a per-sensor
                        setpoint/offset over LoRa; separate from any shared LUT work. */}
                    {isSuperAdmin && sensor.type === '1' && !isSplitRow(sensor) && (
                      <button
                        type="button"
                        onClick={() => setCalibrateSensor(sensor)}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors"
                        title="Send a per-sensor calibration setpoint/offset"
                      >
                        <SlidersHorizontal className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Calibrate</span>
                      </button>
                    )}

                    {/* Set Reference Resistor — superadmin only, type 1 sensors. Sends a
                        per-unit reference-resistor calibration value over LoRa; corrects
                        the actual per-unit hardware source of multi-degree errors (see
                        HyperionComms/CALIBRATION_LOG.md), separate from the CAL: setpoint
                        offset above. */}
                    {isSuperAdmin && sensor.type === '1' && !isSplitRow(sensor) && (
                      <button
                        type="button"
                        onClick={() => setSetRefCalSensor(sensor)}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/40 transition-colors"
                        title="Set this unit's own reference-resistor calibration value"
                      >
                        <Zap className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Set Rref</span>
                      </button>
                    )}

                    {/* Configure channels (dual channel, non-split) */}
                    {canManageSensors && isDualChannel && !isSplitRow(sensor) && (
                      <button
                        type="button"
                        onClick={() => setSplitDialogSensor(sensor)}
                        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                        title="Configure channel names and thresholds"
                      >
                        <Signal className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Channels</span>
                      </button>
                    )}

                    {/* ⋯ more menu */}
                    {canManageSensors && (
                      <div className="ml-auto">
                        <Menu>
                          <MenuButton
                            type="button"
                            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
                            onClick={e => e.stopPropagation()}
                          >
                            <span className="sr-only">More options</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </MenuButton>
                          <MenuItems
                            anchor="bottom end"
                            transition
                            className="z-50 mt-1 w-52 rounded-xl border border-gray-200/90 bg-white p-1 shadow-lg outline-none [--anchor-gap:4px] data-[closed]:scale-95 data-[closed]:opacity-0 dark:border-gray-700 dark:bg-gray-900"
                          >
                            {!isSplitRow(sensor) && (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    type="button"
                                    className={`${focus ? 'bg-gray-50 dark:bg-gray-800' : ''} flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100`}
                                    onClick={() => handleEdit(sensor)}
                                  >
                                    <Pencil className="h-4 w-4 text-gray-400 shrink-0" />
                                    Edit details
                                  </button>
                                )}
                              </MenuItem>
                            )}
                            {!isSplitRow(sensor) && (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    type="button"
                                    className={`${focus ? 'bg-gray-50 dark:bg-gray-800' : ''} flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100`}
                                    onClick={() => handleToggleServiceStatus(sensor)}
                                  >
                                    {isOos ? (
                                      <><Power className="h-4 w-4 text-emerald-500 shrink-0" />Return to service</>
                                    ) : (
                                      <><Wrench className="h-4 w-4 text-gray-400 shrink-0" />Mark out of service</>
                                    )}
                                  </button>
                                )}
                              </MenuItem>
                            )}
                            {!isSplitRow(sensor) && (
                              <>
                                <MenuSeparator className="my-1 border-t border-gray-100 dark:border-gray-700" />
                                <MenuItem>
                                  {({ focus }) => (
                                    <button
                                      type="button"
                                      className={`${focus ? 'bg-red-50 dark:bg-red-950/30' : ''} flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm font-medium text-red-700 dark:text-red-400`}
                                      onClick={() => handleDelete(sensor)}
                                    >
                                      <Trash2 className="h-4 w-4 shrink-0" />
                                      Delete sensor
                                    </button>
                                  )}
                                </MenuItem>
                              </>
                            )}
                            {isSplitRow(sensor) && (
                              <MenuItem>
                                {({ focus }) => (
                                  <button
                                    type="button"
                                    className={`${focus ? 'bg-gray-50 dark:bg-gray-800' : ''} flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-gray-800 dark:text-gray-100`}
                                    onClick={() => setSplitDialogSensor(sensor)}
                                  >
                                    <Signal className="h-4 w-4 text-gray-400 shrink-0" />
                                    Configure channels
                                  </button>
                                )}
                              </MenuItem>
                            )}
                          </MenuItems>
                        </Menu>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Bulk delete sticky bar ───────────────────────────────────────── */}
        {isBulkSelectMode && selectedSensorIds.size > 0 && (
          <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 flex items-center gap-4 rounded-2xl bg-gray-900 px-6 py-3.5 shadow-2xl shadow-black/30 dark:bg-gray-800">
            <span className="text-sm font-medium text-white">
              {selectedSensorIds.size} sensor{selectedSensorIds.size !== 1 ? 's' : ''} selected
            </span>
            {isSuperAdmin && (
              <button
                type="button"
                onClick={() => openFirmwareDialog(selectedFirmwareTargets)}
                disabled={selectedFirmwareTargets.length === 0}
                className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-purple-500 disabled:opacity-50"
                title="Send an OTA firmware update to each selected TS1 sensor"
              >
                <Upload className="h-4 w-4 shrink-0" />
                Update FW ({selectedFirmwareTargets.length})
              </button>
            )}
            <button
              type="button"
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-500"
            >
              <Trash2 className="h-4 w-4 shrink-0" />
              Delete selected
            </button>
          </div>
        )}

        {/* ── Add sensor dialog ─────────────────────────────────────────────── */}
        <Dialog isOpen={isAddingSensor} onClose={() => setIsAddingSensor(false)} panelClassName="max-w-lg">
          <h3 className="mb-5 pr-8 text-lg font-semibold text-gray-900 dark:text-white">Register a new sensor</h3>
          {formError && (
            <div className="mb-4 rounded-lg bg-red-50 p-3 dark:bg-red-900/25">
              <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Sensor type</label>
              <select
                value={formData.type}
                onChange={handleTypeChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 bg-app-inset py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              >
                <option value="">Select type</option>
                {types?.map(type => (
                  <option key={type.type} value={type.type}>{type.description}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Serial number</label>
              <input
                type="text" required value={formData.serialNo} onChange={handleSerialChange}
                disabled={!formData.type} placeholder={getSerialPlaceholder(formData.type)}
                className={`mt-1 block w-full rounded-lg border bg-app-inset py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm ${serialError ? 'border-red-500' : 'border-gray-300'}`}
              />
              {serialError && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{serialError}</p>}
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Friendly name</label>
              <input
                type="text" required value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="mt-1 block w-full rounded-lg border border-gray-300 bg-app-inset py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                Assign to zone <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <select
                value={formData.zoneId || ''}
                onChange={e => setFormData({ ...formData, zoneId: e.target.value ? Number(e.target.value) : undefined })}
                className="mt-1 block w-full rounded-lg border border-gray-300 bg-app-inset py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-sm"
              >
                <option value="">No zone assignment</option>
                {zones?.map(zone => (
                  <option key={zone.zone_id} value={zone.zone_id}>{zone.zone_name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-100 pt-5 dark:border-gray-700">
              <button type="button" onClick={() => setIsAddingSensor(false)} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800">
                Cancel
              </button>
              <button type="submit" className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500">
                Add sensor
              </button>
            </div>
          </form>
        </Dialog>

        {/* ── Bulk add dialog ───────────────────────────────────────────────── */}
        <Dialog
          isOpen={isBulkAdding}
          onClose={() => {
            if (!bulkAddProgress || bulkAddProgress.current >= bulkAddProgress.total) {
              setIsBulkAdding(false);
              setBulkAddProgress(null);
            }
          }}
          panelClassName="max-w-lg"
        >
          <h3 className="mb-1 pr-8 text-lg font-semibold text-gray-900 dark:text-white">Bulk add TS1 sensors</h3>
          <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">Registers a sequential range of serial numbers as sensors in one go.</p>

          {bulkAddProgress ? (
            /* ── Progress view ── */
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                <span>Adding sensors…</span>
                <span className="tabular-nums font-medium">{bulkAddProgress.current} / {bulkAddProgress.total}</span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="h-full rounded-full bg-indigo-600 transition-all duration-200"
                  style={{ width: `${(bulkAddProgress.current / bulkAddProgress.total) * 100}%` }}
                />
              </div>
              {bulkAddProgress.errors.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
                  <p className="mb-1 text-xs font-semibold text-red-700 dark:text-red-400">{bulkAddProgress.errors.length} error{bulkAddProgress.errors.length !== 1 ? 's' : ''}:</p>
                  {bulkAddProgress.errors.map((e, i) => (
                    <p key={i} className="text-xs text-red-600 dark:text-red-400">{e}</p>
                  ))}
                </div>
              )}
              {bulkAddProgress.current >= bulkAddProgress.total && (
                <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-800 dark:bg-emerald-950/30">
                  <span className="text-sm font-medium text-emerald-800 dark:text-emerald-300">
                    Done — {bulkAddProgress.total - bulkAddProgress.errors.length} of {bulkAddProgress.total} added successfully
                  </span>
                  <button
                    type="button"
                    onClick={() => { setIsBulkAdding(false); setBulkAddProgress(null); }}
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-500"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* ── Form view ── */
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Sensor type</label>
                <select
                  value={bulkAddForm.type}
                  onChange={e => setBulkAddForm(f => ({ ...f, type: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-app-inset py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {types?.map(t => (
                    <option key={t.type} value={t.type}>{t.description}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Name prefix</label>
                <input
                  type="text"
                  value={bulkAddForm.namePrefix}
                  onChange={e => setBulkAddForm(f => ({ ...f, namePrefix: e.target.value }))}
                  placeholder="e.g. Sensor"
                  className="mt-1 block w-full rounded-lg border border-gray-300 bg-app-inset py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-400">Each sensor will be named "{bulkAddForm.namePrefix.trim() || 'Sensor'} 131250", etc.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">Start serial</label>
                  <input
                    type="number"
                    value={bulkAddForm.startSerial}
                    onChange={e => setBulkAddForm(f => ({ ...f, startSerial: e.target.value }))}
                    placeholder="131250"
                    className="mt-1 block w-full rounded-lg border border-gray-300 bg-app-inset py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">End serial</label>
                  <input
                    type="number"
                    value={bulkAddForm.endSerial}
                    onChange={e => setBulkAddForm(f => ({ ...f, endSerial: e.target.value }))}
                    placeholder="131299"
                    className="mt-1 block w-full rounded-lg border border-gray-300 bg-app-inset py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              {(() => {
                const s = parseInt(bulkAddForm.startSerial, 10);
                const e = parseInt(bulkAddForm.endSerial, 10);
                if (!isNaN(s) && !isNaN(e) && e >= s) {
                  const count = e - s + 1;
                  return (
                    <div className={`rounded-lg px-4 py-3 text-sm ${count > 500 ? 'border border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-400' : 'border border-indigo-100 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/30 dark:text-indigo-300'}`}>
                      {count > 500
                        ? `${count} sensors — exceeds the 500 sensor limit. Please narrow the range.`
                        : `Will add ${count} sensor${count !== 1 ? 's' : ''} (serials ${s} to ${e})`}
                    </div>
                  );
                }
                return null;
              })()}
              <div className="flex justify-end gap-3 border-t border-gray-100 pt-5 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsBulkAdding(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleBulkAdd}
                  disabled={(() => {
                    const s = parseInt(bulkAddForm.startSerial, 10);
                    const e = parseInt(bulkAddForm.endSerial, 10);
                    return isNaN(s) || isNaN(e) || s > e || e - s >= 500;
                  })()}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Add sensors
                </button>
              </div>
            </div>
          )}
        </Dialog>

        {/* ── Edit sensor modal (name + type) ───────────────────────────────── */}
        <Dialog isOpen={isEditModalOpen} onClose={handleEditModalCancel}>
          <form onSubmit={handleEditModalSave} className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Edit sensor details</h3>
            {formError && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 p-3 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sensor Name</label>
              <input type="text" name="name" required value={editFormData.name} onChange={handleEditFormChange}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-app-inset" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sensor Type</label>
              <select name="type" value={editFormData.type} onChange={handleEditFormChange}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-app-inset">
                <option value="">Select type</option>
                {types?.map(type => (
                  <option key={type.type} value={type.type}>{type.description}</option>
                ))}
              </select>
            </div>
            {editFormData.type === '1' && (() => {
              const editingSensor = sensors?.find(s => s.id === editFormData.id);
              return editingSensor?.firmware_version ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Firmware Version</label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">v{editingSensor.firmware_version}</p>
                </div>
              ) : null;
            })()}
            {editFormData.type === '1' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Probe Type</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{editFormData.probeType}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Fixed by the sensor's firmware build. To change it, a super admin sends the other build with Update FW.
                </p>
                {(() => {
                  // The value above is the build last sent to the sensor — it doesn't
                  // confirm the sensor actually applied it. This reads back what the
                  // sensor itself reported on its most recent reading (data_type, from
                  // the TY: field it now appends over LoRa) so a stuck/missed probe-type
                  // change is visible here instead of the dashboard just trusting the
                  // setting regardless of what the sensor actually sent.
                  const editingSensor = sensors?.find(s => s.id === editFormData.id);
                  const editingReading = editingSensor
                    ? liveDataFeed.find(d => Number(d.sensor_id) === editingSensor.serialNo)
                    : undefined;
                  const reportedType =
                    editingReading?.data_type === 'external_temp' ? 'external'
                    : editingReading?.data_type === 'internal_temp' ? 'internal'
                    : null;
                  if (!reportedType) {
                    return (
                      <p className="mt-2 text-xs italic text-gray-400 dark:text-gray-500">
                        Sensor hasn't reported a probe type in its recent data yet.
                      </p>
                    );
                  }
                  const mismatch = reportedType !== editFormData.probeType;
                  return (
                    <p className={`mt-2 text-xs ${mismatch ? 'font-medium text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      Sensor last reported: <strong className="capitalize">{reportedType}</strong>
                      {mismatch && ' — doesn\'t match the build last sent. The firmware update may not have completed yet.'}
                    </p>
                  );
                })()}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Calibration Date</label>
              <input type="date" name="calibrationDate" value={editFormData.calibrationDate} onChange={handleEditFormChange}
                className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm bg-app-inset" />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {editFormData.calibrationDate
                  ? `Due: ${new Date(new Date(editFormData.calibrationDate).setMonth(new Date(editFormData.calibrationDate).getMonth() + 12)).toLocaleDateString()} (12 months after calibration)`
                  : 'Not calibrated yet'}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={handleEditModalCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md">
                Cancel
              </button>
              <button type="submit" className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md">
                Save
              </button>
            </div>
          </form>
        </Dialog>

        {/* ── Split channel dialog ──────────────────────────────────────────── */}
        {splitDialogSensor && (
          <SplitChannelDialog sensor={splitDialogSensor} onClose={() => setSplitDialogSensor(null)} />
        )}

        {/* ── Calibrate dialog ──────────────────────────────────────────────── */}
        <CalibrateSensorDialog sensor={calibrateSensor} onClose={() => setCalibrateSensor(null)} user={user} />
        <SetRefCalDialog sensor={setRefCalSensor} onClose={() => setSetRefCalSensor(null)} user={user} />

        {/* ── Firmware update dialog ────────────────────────────────────────── */}
        <Dialog isOpen={firmwareDialogSensors.length > 0} onClose={() => { if (!firmwareSubmitting) setFirmwareDialogSensors([]); }} panelClassName="max-w-md">
          <h3 className="mb-1 pr-8 text-lg font-semibold text-gray-900 dark:text-white">
            {firmwareDialogSensors.length > 1 ? `Update Firmware on ${firmwareDialogSensors.length} Sensors` : 'Update Sensor Firmware'}
          </h3>
          {firmwareDialogSensors.length === 1 && (
            <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">{firmwareDialogSensors[0].name}</span>
              {' · '}serial {firmwareDialogSensors[0].serialNo}
              {firmwareDialogSensors[0].firmware_version && <> · current v{firmwareDialogSensors[0].firmware_version}</>}
            </p>
          )}
          {(firmwareDialogSensors.length > 1 || Object.keys(firmwareResults).length > 0) && (
            <ul className="mb-5 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 text-xs dark:divide-gray-800 dark:border-gray-700">
              {firmwareDialogSensors.map(s => {
                const res = firmwareResults[s.id];
                return (
                  <li key={s.id} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-gray-700 dark:text-gray-300">
                      {s.name} <span className="font-mono text-gray-400">{s.serialNo}</span>
                    </span>
                    <span className="font-mono text-gray-400">{s.firmware_version ? `v${s.firmware_version}` : '—'} · {s.probe_type ?? 'internal'}</span>
                    {res && (
                      <span className={`max-w-[45%] truncate font-medium ${res.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`} title={res.text}>
                        {res.ok ? `→ ${res.text}` : res.text}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {firmwareSuccess ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30 p-4 text-sm text-emerald-700 dark:text-emerald-400">
              OTA command sent{firmwareDialogSensors.length > 1 ? ` to all ${firmwareDialogSensors.length} sensors` : ''}
              {firmwareDialogSensors.length === 1 && firmwareResults[firmwareDialogSensors[0].id] && `: ${firmwareResults[firmwareDialogSensors[0].id].text}-probe build`}.
              Each sensor updates on its next check-in; the FW badge on its card shows the version it reports afterwards.
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Route via base unit
                </label>
                <select
                  value={firmwareBaseUnitSerial}
                  onChange={e => setFirmwareBaseUnitSerial(e.target.value)}
                  className="block w-full rounded-lg border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white py-2 px-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40"
                >
                  {baseUnits.length === 0 && <option value="">No base units found</option>}
                  {baseUnits.map(bu => (
                    <option key={bu.id} value={String(bu.serialNo)}>
                      {bu.base_unit_name} ({bu.serialNo})
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  The base unit must be in RF range of the sensor to relay the update command.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Probe
                </label>
                <select
                  value={firmwareProbeType}
                  onChange={e => setFirmwareProbeType(e.target.value as 'keep' | 'internal' | 'external')}
                  className="block w-full rounded-lg border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white py-2 px-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40"
                >
                  {firmwareDialogSensors.length > 1 && <option value="keep">Keep each sensor's current probe</option>}
                  <option value="internal">Internal thermistor</option>
                  <option value="external">External probe</option>
                </select>
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  The probe is fixed by the firmware build: this picks which build the sensor downloads. Only sensors
                  that have been serial-flashed with the patched bootloader can be updated over the air.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  WiFi SSID
                </label>
                <input
                  type="text"
                  value={firmwareWifiSsid}
                  onChange={e => setFirmwareWifiSsid(e.target.value)}
                  placeholder="Network name"
                  className="block w-full rounded-lg border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white py-2 px-3 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40"
                />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  The sensor will connect to this network to download the firmware.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  WiFi Password
                </label>
                <div className="relative">
                  <input
                    type={firmwareShowPassword ? 'text' : 'password'}
                    value={firmwareWifiPassword}
                    onChange={e => setFirmwareWifiPassword(e.target.value)}
                    placeholder="Network password"
                    className="block w-full rounded-lg border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white py-2 pl-3 pr-10 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40"
                  />
                  <button
                    type="button"
                    onClick={() => setFirmwareShowPassword(p => !p)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    tabIndex={-1}
                  >
                    {firmwareShowPassword ? (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
              {firmwareError && (
                <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                  {firmwareError}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setFirmwareDialogSensors([])}
                  disabled={firmwareSubmitting}
                  className="rounded-lg border border-gray-200 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleFirmwareSubmit}
                  disabled={firmwareSubmitting || !firmwareBaseUnitSerial || !firmwareWifiSsid || !firmwareWifiPassword}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-500 disabled:opacity-50 transition-colors"
                >
                  {firmwareSubmitting ? (
                    <>Sending&hellip;</>
                  ) : (
                    <><Upload className="h-4 w-4" />{Object.keys(firmwareResults).length > 0 ? 'Retry' : 'Send update'}</>
                  )}
                </button>
              </div>
            </div>
          )}
        </Dialog>

        <AccessDeniedDialog
          isOpen={!!accessDeniedAction}
          onClose={() => setAccessDeniedAction(null)}
          action={accessDeniedAction ?? undefined}
        />

      </div>
    </div>
  );
}
